import React from 'react';
import className from 'classnames';
import { connect } from 'react-redux';
import expandImage from '../../../img/expand-icon-white.svg';
import collapseImage from '../../../img/collapse-icon-white.svg';
import ExternalAnchorLink from '../../frontend-common-libs/src/components/common/buttons/ExternalAnchorLink';
import { ReduxState } from '../../types';
import HelpCenter, { docHelpPaths } from '../../utils/HelpCenter';
import { readOrInitExploreSidebarIsExpanded, writeExploreSidebar } from './exploreSidebarStorage';

type SideBarProps = {
  username: string;
};
type SideBarState = {
  isExpanded: boolean;
};

export class ExploreSideBarV2Impl extends React.Component<SideBarProps, SideBarState> {
  static readonly CONTENT_VERSION = 2;

  private readonly helpCenter: HelpCenter;

  toggleRef: any;

  constructor(props: SideBarProps) {
    super(props);
    this.state = {
      isExpanded: readOrInitExploreSidebarIsExpanded(
        props.username,
        ExploreSideBarV2Impl.CONTENT_VERSION
      )
    };
    this.toggleRef = React.createRef();
    this.helpCenter = new HelpCenter();
  }

  onExpandButtonClick = () => {
    if (this.toggleRef.current) this.toggleRef.current.focus();

    this.setState(
      state => ({ isExpanded: !state.isExpanded }),
      () => {
        const { username } = this.props;
        const { isExpanded } = this.state;
        writeExploreSidebar(username, ExploreSideBarV2Impl.CONTENT_VERSION, isExpanded);
      }
    );
  };

  renderSidebarLink = (id: string, helpPath: string, label: string, description?: string) => (
    <React.Fragment key={id}>
      <ExternalAnchorLink
        id={id}
        href={this.helpCenter.helpUrl(helpPath)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </ExternalAnchorLink>
      {description && <p className="explore-sidebar-description">{description}</p>}
    </React.Fragment>
  );

  renderExpandedSideBar = () => {
    const featureLinks = [
      {
        id: 'advanced-image-analysis-link',
        helpPath: docHelpPaths.Home_New,
        label: 'Advanced Image Analysis',
        description:
          'Automate your ChemiDoc Pro Analysis with built-in relative band quantification.'
      },
      {
        id: 'organization-link',
        helpPath: docHelpPaths.Data_Sharing,
        label: 'Data Sharing',
        description: 'Share qPCR and PCR runs and protocols with other users in your organization.'
      },
      {
        id: 'fleet-management-link',
        helpPath: docHelpPaths.Fleet_Management,
        label: 'PTC Tempo Fleet Management',
        description: 'Reserve and remotely control multiple PTC Tempo PCR instruments.'
      }
    ];

    return (
      <div className="scroll-section">
        <h2 id="explore-sidebar-title">EXPLORE BR.io</h2>

        <br />

        {this.renderSidebarLink('getting-started-link', docHelpPaths.Home, 'Getting Started')}

        <br />
        <br />
        <div>
          {featureLinks.map(({ id, helpPath, label, description }) =>
            this.renderSidebarLink(id, helpPath, label, description)
          )}
        </div>
      </div>
    );
  };

  renderToggleButton = (isExpanded: boolean) => {
    const { img, title } = isExpanded
      ? { img: collapseImage, title: 'collapse' }
      : { img: expandImage, title: 'expand' };
    return (
      <div className="no-scroll-section">
        <button
          ref={this.toggleRef}
          id="explore-sidebar-toggle"
          type="button"
          className="expand-btn"
          onClick={isExpanded ? this.onExpandButtonClick : undefined}
        >
          <img id="toggle-image" src={img} className="expand-img" alt={title} title={title} />
        </button>
      </div>
    );
  };

  renderCollapsedTitle = () => (
    <h4 id="explore-sidebar-title-collapsed">
      EXPLORE
      <br />
      BR.io
    </h4>
  );

  onContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Only the collapsed container is interactive; allow Enter/Space to expand.
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onExpandButtonClick();
    }
  };

  render() {
    const { isExpanded } = this.state;
    // Interactive props are only applied when collapsed so the container acts
    // as a large click/keyboard target to re-expand the sidebar.
    const interactiveProps = isExpanded
      ? {}
      : {
          onClick: this.onExpandButtonClick,
          onKeyDown: this.onContainerKeyDown,
          role: 'button',
          tabIndex: 0,
          'aria-label': 'Expand sidebar'
        };
    return (
      <div
        className={className('explore-sidebar', 'landing-page-v2', {
          expanded: isExpanded
        })}
        {...interactiveProps}
      >
        {this.renderToggleButton(isExpanded)}
        {isExpanded ? this.renderExpandedSideBar() : this.renderCollapsedTitle()}
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    username: state.auth.get('username')
  };
}

export default connect(mapStateToProps)(ExploreSideBarV2Impl);
